import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  ChartPoint,
  DashboardTiles,
  EnrichLeadResponse,
  LeadFeedback,
  LeadPipeline,
  LeadPipelineExportEntity,
  LeadPipelineLeadStatus,
  LeadPipelineListItem,
  LeadPipelineTabCounts,
  PaginationMeta,
  PipelineActivity,
  PipelineCompany,
  PipelineExport,
  PipelineLead,
  PipelineLeadDetail,
  PipelineNotification,
  PipelinePerson,
  PipelineRun,
  PipelineRunDetail,
  PreviewCountsResponse,
  StartExportResponse,
  UnlockEmailResponse,
  VeridionFilterPayload,
} from '../../models/lead-pipeline.model';
import {
  ActivityFilters,
  CompaniesFilters,
  DashboardChartRange,
  LeadsTab,
  ListFiltersState,
  PersonsFilters,
  WizardMode,
} from './lead-pipeline.state';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListActions = createActionGroup({
  source: 'Lead Pipeline/List',
  events: {
    'Load Requested': emptyProps(),
    Loaded: props<{ items: LeadPipelineListItem[]; meta: PaginationMeta; tabs: LeadPipelineTabCounts }>(),
    Failed: props<{ error: any }>(),
    'Filters Updated': props<{ filters: Partial<ListFiltersState> }>(),
    'Include Archived Toggled': props<{ includeArchived: boolean }>(),
  },
});

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

export const WizardActions = createActionGroup({
  source: 'Lead Pipeline/Wizard',
  events: {
    Entered: props<{ mode: WizardMode; uuid?: string }>(),
    'Session Started': props<{ sessionId: string }>(),
    'Step Changed': props<{ step: 1 | 2 | 3 }>(),
    'Filters Updated': props<{ filters: VeridionFilterPayload }>(),
    'Reset Filters': emptyProps(),
    'Volume Selected': props<{ leadsPerDay: 5 | 10 }>(),
    'Workspace Selected': props<{ workspaceId: string }>(),
    'Title Changed': props<{ title: string }>(),
    'Description Changed': props<{ description: string }>(),
    // preview-counts pipeline
    'Preview Counts Requested': props<{ filters: VeridionFilterPayload }>(),
    // user-initiated retry of the current filters (bypasses the debounce/distinct pipeline)
    'Preview Retry Requested': emptyProps(),
    'Preview Counts Loaded': props<{ counts: PreviewCountsResponse }>(),
    'Preview Rate Limited': props<{ retryAfter: number }>(),
    'Preview Failed': props<{ error: any }>(),
    // edit prefill
    'Edit Loaded': props<{ pipeline: LeadPipeline }>(),
    // launch (create)
    'Launch Requested': emptyProps(),
    'Launch Succeeded': props<{ pipeline: LeadPipeline }>(),
    'Launch Failed': props<{ error: any }>(),
    // update (edit)
    'Update Requested': emptyProps(),
    'Update Succeeded': props<{ pipeline: LeadPipeline }>(),
    'Update Failed': props<{ error: any }>(),
    // teardown
    Cleared: emptyProps(),
  },
});

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

export const DetailActions = createActionGroup({
  source: 'Lead Pipeline/Detail',
  events: {
    Requested: props<{ uuid: string }>(),
    Loaded: props<{ pipeline: LeadPipeline }>(),
    Failed: props<{ error: any }>(),
    Refreshed: props<{ uuid: string }>(),
    Cleared: emptyProps(),
  },
});

// ---------------------------------------------------------------------------
// Lifecycle (pause / resume / archive / delete)
// ---------------------------------------------------------------------------

export const LifecycleActions = createActionGroup({
  source: 'Lead Pipeline/Lifecycle',
  events: {
    'Pause Requested': props<{ uuid: string }>(),
    'Resume Requested': props<{ uuid: string }>(),
    'Archive Requested': props<{ uuid: string }>(),
    Mutated: props<{ pipeline: LeadPipeline }>(),
    'Delete Requested': props<{ uuid: string }>(),
    // detail page: archive (if needed) then hard-delete, then navigate to the list
    'Archive And Delete Requested': props<{ uuid: string }>(),
    Deleted: props<{ uuid: string }>(),
    Failed: props<{ error: any }>(),
  },
});

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export const DashboardActions = createActionGroup({
  source: 'Lead Pipeline/Dashboard',
  events: {
    Requested: props<{ uuid: string }>(),
    Loaded: props<{ tiles: DashboardTiles; chart: ChartPoint[] }>(),
    Failed: props<{ error: any }>(),
    'Chart Range Changed': props<{ range: DashboardChartRange }>(),
  },
});

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export const CompaniesActions = createActionGroup({
  source: 'Lead Pipeline/Companies',
  events: {
    Requested: props<{ uuid: string }>(),
    'Load More Requested': props<{ uuid: string }>(),
    'Page Changed': props<{ uuid: string; page: number }>(),
    Loaded: props<{ items: PipelineCompany[]; meta: PaginationMeta; append: boolean }>(),
    Failed: props<{ error: any }>(),
    'Filters Updated': props<{ filters: Partial<CompaniesFilters> }>(),
  },
});

// ---------------------------------------------------------------------------
// Persons
// ---------------------------------------------------------------------------

export const PersonsActions = createActionGroup({
  source: 'Lead Pipeline/Persons',
  events: {
    Requested: props<{ uuid: string }>(),
    'Load More Requested': props<{ uuid: string }>(),
    'Page Changed': props<{ uuid: string; page: number }>(),
    Loaded: props<{ items: PipelinePerson[]; meta: PaginationMeta; append: boolean }>(),
    Failed: props<{ error: any }>(),
    'Filters Updated': props<{ filters: Partial<PersonsFilters> }>(),
    'Unlock Email Requested': props<{ uuid: string; personUuid: string }>(),
    'Email Unlocked': props<{ result: UnlockEmailResponse }>(),
    'Unlock Email Failed': props<{ personUuid: string; error: any }>(),
  },
});

// ---------------------------------------------------------------------------
// Qualified leads
// ---------------------------------------------------------------------------

export const LeadsActions = createActionGroup({
  source: 'Lead Pipeline/Leads',
  events: {
    Requested: props<{ uuid: string }>(),
    'Load More Requested': props<{ uuid: string }>(),
    Loaded: props<{ items: PipelineLead[]; meta: PaginationMeta; append: boolean }>(),
    Failed: props<{ error: any }>(),
    'Tab Changed': props<{ tab: LeadsTab }>(),
    'Sort Changed': props<{ sort: string }>(),
    // detail drawer
    'Detail Requested': props<{ uuid: string; leadUuid: string }>(),
    'Detail Loaded': props<{ lead: PipelineLeadDetail }>(),
    'Detail Failed': props<{ error: any }>(),
    'Drawer Closed': emptyProps(),
    // mutations
    'Status Update Requested': props<{ uuid: string; leadUuid: string; status: LeadPipelineLeadStatus }>(),
    'Status Updated': props<{ lead: PipelineLead }>(),
    'Feedback Requested': props<{ uuid: string; leadUuid: string; feedback: LeadFeedback; notes?: string | null }>(),
    'Feedback Submitted': props<{ lead: PipelineLead }>(),
    'Enrich Requested': props<{ uuid: string; leadUuid: string; workspaceId: string }>(),
    Enriched: props<{ result: EnrichLeadResponse }>(),
    'Enrich Failed': props<{ error: any }>(),
    'Copy To Crm Requested': props<{ uuid: string; leadUuid: string }>(),
    'Copied To Crm': props<{ leadUuid: string }>(),
    'Copy To Crm Failed': props<{ error: any }>(),
    'Enhance Requested': props<{ uuid: string; leadUuid: string }>(),
    Enhanced: props<{ leadUuid: string; reportId: number | null }>(),
    'Enhance Failed': props<{ error: any }>(),
    'Send Email Requested': props<{ uuid: string; leadUuid: string }>(),
    'Send Email Recorded': props<{ leadUuid: string }>(),
  },
});

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

export const ActivityActions = createActionGroup({
  source: 'Lead Pipeline/Activity',
  events: {
    Requested: props<{ uuid: string }>(),
    'Load More Requested': props<{ uuid: string }>(),
    Loaded: props<{ items: PipelineActivity[]; meta: PaginationMeta; append: boolean }>(),
    Failed: props<{ error: any }>(),
    'Filter Changed': props<{ filters: Partial<ActivityFilters> }>(),
  },
});

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

export const RunsActions = createActionGroup({
  source: 'Lead Pipeline/Runs',
  events: {
    Requested: props<{ uuid: string }>(),
    'Load More Requested': props<{ uuid: string }>(),
    Loaded: props<{ items: PipelineRun[]; meta: PaginationMeta; append: boolean }>(),
    Failed: props<{ error: any }>(),
    'Detail Requested': props<{ uuid: string; runUuid: string }>(),
    'Detail Loaded': props<{ run: PipelineRunDetail }>(),
    'Detail Failed': props<{ error: any }>(),
    'Detail Closed': emptyProps(),
  },
});

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const NotificationsActions = createActionGroup({
  source: 'Lead Pipeline/Notifications',
  events: {
    Requested: props<{ uuid: string }>(),
    Loaded: props<{ items: PipelineNotification[]; unreadCount: number }>(),
    Failed: props<{ error: any }>(),
  },
});

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export const ExportsActions = createActionGroup({
  source: 'Lead Pipeline/Exports',
  events: {
    Requested: props<{ uuid: string }>(),
    Loaded: props<{ items: PipelineExport[] }>(),
    Failed: props<{ error: any }>(),
    'Create Requested': props<{ uuid: string; entity: LeadPipelineExportEntity; filtersApplied?: Record<string, unknown> }>(),
    'Create Started': props<{ export: StartExportResponse }>(),
    'Create Failed': props<{ entity: LeadPipelineExportEntity; error: any }>(),
    // polling
    'Export Updated': props<{ export: PipelineExport }>(),
    'Export Failed': props<{ exportUuid: string; error: any }>(),
    // download (fetches the signed url then opens it)
    'Download Requested': props<{ exportUuid: string }>(),
  },
});

// ---------------------------------------------------------------------------
// Run-now
// ---------------------------------------------------------------------------

export const RunNowActions = createActionGroup({
  source: 'Lead Pipeline/RunNow',
  events: {
    'Confirm Modal Opened': props<{ uuid: string }>(),
    'Confirm Modal Closed': emptyProps(),
    // `auto` marks a run kicked off automatically (e.g. right after pipeline
    // creation) rather than by the user pressing "Get Leads" — used to pick a
    // non-"manual" toast.
    Requested: props<{ uuid: string; auto?: boolean }>(),
    Dispatched: props<{ uuid: string; runUuid: string; creditsDeducted?: number }>(),
    'In Flight': props<{ uuid: string; runUuid: string }>(),
    Failed: props<{ uuid: string; error: any }>(),
    Completed: props<{ uuid: string }>(),
  },
});
